import { useRef, useState } from "react";

export const useRecording = () => {
  const recordingTimestamp = useRef(0);
  const [isLoadingRecording, setIsLoadingRecording] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const recording = useRef<MediaRecorder | null>(null);
  const recordingChunks = useRef<BlobPart[]>([]);

  const start = async (retry = 0) => {
    try {
      setIsLoadingRecording(true);

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      // Try to use WAV format if supported, otherwise fall back to WebM
      const options: MediaRecorderOptions = {};
      if (MediaRecorder.isTypeSupported("audio/wav")) {
        options.mimeType = "audio/wav";
      } else if (MediaRecorder.isTypeSupported("audio/webm;codecs=opus")) {
        options.mimeType = "audio/webm;codecs=opus";
      }

      recording.current = new MediaRecorder(stream, options);
      recordingChunks.current = [];

      recording.current.ondataavailable = (e) => {
        if (e.data.size > 0) recordingChunks.current.push(e.data);
      };

      recording.current.start();

      recordingTimestamp.current = Date.now();

      setIsLoadingRecording(false);
      setIsRecording(true);
    } catch (error) {
      if (retry < 3) {
        setTimeout(() => {
          start(retry + 1);
        }, 1000);
      } else {
        setIsLoadingRecording(false);
        alert("Failed to record your voice. Please try again later.");
      }
    }
  };

  const stop = async () => {
    return new Promise<File | null>((resolve) => {
      if (!recording.current || !isRecording) {
        resolve(null);
        return;
      }

      const duration = Date.now() - recordingTimestamp.current;
      if (duration < 1000) {
        resolve(null);
        return;
      }

      // Set up the stop event handler before stopping
      recording.current.onstop = () => {
        // Determine the actual mime type from the MediaRecorder
        const mimeType = recording.current?.mimeType || "audio/webm";
        const blob = new Blob(recordingChunks.current, { type: mimeType });

        // Create appropriate filename based on format
        const extension = mimeType.includes("wav") ? "wav" : "webm";
        const file = new File([blob], `recording.${extension}`, { type: mimeType });

        setIsRecording(false);
        resolve(file);
      };

      // Stop the recording
      recording.current.stop();

      // Stop all tracks to release the microphone
      recording.current.stream.getTracks().forEach((track) => track.stop());
    });
  };

  return {
    start,
    stop,
    isLoadingRecording,
    isRecording,
  };
};
